/* Keep the cart page and drawer note in sync. This endpoint only receives { note }. */
(() => {
  if (window.LostFoundCartNote) return;
  const inputs = () => Array.from(document.querySelectorAll('[data-cart-note]'));
  const initial = inputs()[0];
  if (!initial) return;

  let desired = initial.value;
  let saved = desired;
  let state = '';
  let timer;
  let pending = null;
  let navigating = false;
  let edited = false;
  const nativeSubmissions = new WeakSet();

  const revealField = (field) => {
    const disclosure = field?.closest?.('details');
    if (disclosure) disclosure.open = true;
  };

  const showStatus = (next) => {
    state = next;
    document.querySelectorAll('[data-cart-note-field]').forEach((field) => {
      if (next === 'error') revealField(field);
      field.dataset.noteState = next;
      const status = field.querySelector('[data-cart-note-status]');
      const retry = field.querySelector('[data-cart-note-retry]');
      if (status) status.textContent = next ? field.getAttribute('data-note-' + next) || '' : '';
      if (retry) retry.hidden = next !== 'error';
    });
  };

  const mirror = () => {
    inputs().forEach((input) => { if (input.value !== desired) input.value = desired; });
  };

  const endpoint = () => {
    const field = inputs()[0];
    const base = field && field.getAttribute('data-update-url');
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    const url = new URL(base || root + 'cart/update.js', window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Invalid cart note endpoint');
    if (!url.pathname.endsWith('.js')) url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    return url.pathname + url.search;
  };

  const postNote = async (url, note, allowFallback = true) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let response;
    let text;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
        signal: controller.signal,
      });
      text = await response.text();
    } finally {
      clearTimeout(timeout);
    }
    // This shop's Japanese localized endpoint can return an HTML 404 in the CLI proxy.
    const isJapaneseEndpoint = /^\/ja(?:-[a-z]{2})?\/cart\/update\.js(?:\?|$)/i.test(url);
    const isHtml = /text\/html|application\/xhtml\+xml/i.test(response.headers.get('content-type') || '') || /^\s*<(?:!doctype|html)/i.test(text);
    if (allowFallback && isJapaneseEndpoint && response.status === 404 && isHtml) {
      return postNote('/cart/update.js', note, false);
    }
    let cart;
    try { cart = JSON.parse(text); } catch (error) { throw new Error('Invalid cart note response'); }
    if (!response.ok || !cart || typeof cart !== 'object' || !Object.prototype.hasOwnProperty.call(cart, 'note')) {
      throw new Error('Cart note was not saved');
    }
    return cart;
  };

  const flush = () => {
    clearTimeout(timer);
    if (pending) return pending;
    if (saved === desired) {
      if (state === 'saving' || state === 'error') showStatus('saved');
      return Promise.resolve();
    }
    pending = (async () => {
      try {
        while (saved !== desired) {
          const note = desired;
          showStatus('saving');
          await postNote(endpoint(), note);
          saved = note;
          document.dispatchEvent(new CustomEvent('cart:note-saved', { detail: { note } }));
        }
        showStatus('saved');
      } catch (error) {
        // A timeout may happen after Shopify accepted the write. Re-confirm even if
        // the shopper changes the text back to the value saved before that request.
        saved = null;
        showStatus('error');
        throw error;
      } finally {
        pending = null;
      }
    })();
    return pending;
  };

  const sync = (note) => {
    // Keep this page's edits authoritative: an older cart fetch can arrive after a save.
    if (edited || pending || desired !== saved) { mirror(); return; }
    desired = typeof note === 'string' ? note : '';
    saved = desired;
    mirror();
  };

  const showFailure = (context) => {
    const field = context.closest('[data-cart-note-field]') || context.querySelector('[data-cart-note-field]') || document.querySelector('[data-cart-note-field]');
    revealField(field);
    const input = field && field.querySelector('[data-cart-note]');
    if (input) input.focus();
  };

  const afterSave = async (context, proceed) => {
    if (navigating) return;
    navigating = true;
    const wasInert = context.inert;
    context.inert = true;
    context.setAttribute('aria-busy', 'true');
    try {
      if (window.LostFoundCart) await window.LostFoundCart.flush();
      await flush();
      proceed();
    } catch (error) {
      context.inert = wasInert;
      showFailure(context);
    } finally {
      context.inert = wasInert;
      context.removeAttribute('aria-busy');
      navigating = false;
    }
  };

  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-cart-note]')) return;
    edited = true;
    desired = event.target.value;
    mirror();
    clearTimeout(timer);
    if (desired === saved && !pending) { showStatus('saved'); return; }
    showStatus('saving');
    timer = setTimeout(() => { flush().catch(() => {}); }, 500);
  });

  document.addEventListener('click', (event) => {
    const retry = event.target.closest('[data-cart-note-retry]');
    if (retry) {
      event.preventDefault();
      flush().catch(() => showFailure(retry));
      return;
    }
    const checkout = event.target.closest('a[data-cart-note-checkout], a.cart-drawer__viewcart');
    if (!checkout || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    afterSave(checkout.closest('.cart-drawer') || checkout, () => { window.location.assign(checkout.href); });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches('[data-cart-note-form]')) return;
    if (nativeSubmissions.has(form)) { nativeSubmissions.delete(form); return; }
    event.preventDefault();
    const submitter = event.submitter;
    afterSave(form, () => {
      nativeSubmissions.add(form);
      if (typeof form.requestSubmit === 'function') {
        if (submitter && submitter.form === form) form.requestSubmit(submitter);
        else form.requestSubmit();
      } else {
        if (submitter && submitter.name) {
          const action = document.createElement('input');
          action.type = 'hidden'; action.name = submitter.name; action.value = submitter.value;
          form.appendChild(action);
        }
        HTMLFormElement.prototype.submit.call(form);
      }
    });
  });

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && Object.prototype.hasOwnProperty.call(cart, 'note')) sync(cart.note);
  });

  window.LostFoundCartNote = { flush, sync };
  mirror();
})();
